/**
 * Landing page — `/`.
 *
 * The first prerendered surface a search engine or AI crawler reads. Real
 * text above the fold, a hero screenshot with explicit dimensions, what
 * the tool does, the learn pages, the free-tier facts and one call to
 * action. Claims are validated facts only (framing V4, section 9): no
 * uniqueness, superiority or social-proof statements.
 */

import Link from '../Link';
import { SCREENSHOT } from '../../lib/screenshot';
import { LEARN_ROUTE_LIST } from '../../lib/route';
import { TIER_LIMIT } from '../../lib/tier';
import './LandingPage.css';

interface LandingPageProps {
  onSignup: () => void;
}

const CAPABILITY_LIST = [
  {
    title: 'Import the corridor you already have',
    text: 'Load intersections, spacing, speeds, offsets and phase splits from a CSV, a SigTDD JSON export or a Synchro UTDF file. The diagram appears as soon as the file is read.',
  },
  {
    title: 'See the green wave form while you adjust',
    text: 'Drag the offset slider or type a value and the time-distance diagram redraws immediately, with the progression band, next-green band and phase-extent lines you choose to show.',
  },
  {
    title: 'Keep several timing plans per corridor',
    text: 'AM peak, PM peak and off-peak plans share one set of intersection geometry. Switch, duplicate and rename plans from the toolbar.',
  },
  {
    title: 'Let the tool set the offsets',
    text: 'Auto-sequence calculates offsets from spacing and design speed for northbound or southbound progression; you refine from there.',
  },
  {
    title: 'Share a read-only link',
    text: 'Reviewers open the diagram in their browser without an account. Revoke the link when the review is over.',
  },
  {
    title: 'Export for the report',
    text: 'CSV and JSON for data, a dark or light copy of the diagram for slides, and a PDF with diagram and data table on the Pro tier.',
  },
];

export default function LandingPage({ onSignup }: LandingPageProps) {
  const free = TIER_LIMIT.free;
  const hero = SCREENSHOT.riversidePmPeak;

  return (
    <main className="landing">
      <div className="landing-container">
        <header className="landing-hero">
          <h1>Time-distance diagrams for traffic signal coordination, in the browser</h1>
          <p className="landing-lede">
            Build a corridor, watch the green wave form as you adjust offsets, and share the
            time-space diagram with a link. No installation. Free tier included.
          </p>
          <div className="landing-cta">
            <Link to="/app" className="landing-btn landing-btn-primary">Open the tool</Link>
            <button className="landing-btn landing-btn-secondary" onClick={onSignup}>Create a free account</button>
          </div>
          <p className="landing-note">Runs in any current desktop browser. Nothing to install, no credit card for the free tier.</p>
        </header>

        <figure className="landing-figure">
          <img
            src={hero.src}
            width={hero.width}
            height={hero.height}
            alt={hero.alt}
            fetchPriority="high"
            decoding="async"
          />
          <figcaption>{hero.caption}</figcaption>
        </figure>

        <section className="landing-section" aria-labelledby="landing-what">
          <h2 id="landing-what">What a time-distance diagram shows</h2>
          <p>
            A time-distance diagram, called a time-space diagram in North American practice, plots
            distance along an arterial on the vertical axis and time on the horizontal axis. Each
            signalised intersection is a horizontal bar of green, amber and red phases repeating
            every cycle; a vehicle travelling at the design speed is a sloped line. When the offsets
            are right, the sloped band passes through green at every intersection: that band is the
            green wave, and its width in seconds is the bandwidth. SigTDD draws all of it live from
            the numbers in the intersection table.
          </p>
          <p>
            <Link to="/learn/time-distance-diagram" event="learn-open" source="landing">Read the full explanation</Link>
            {' '}or{' '}
            <Link to="/learn/worked-example-corridor" event="learn-open" source="landing">follow the worked example</Link>.
          </p>
        </section>

        <section className="landing-section" aria-labelledby="landing-capability">
          <h2 id="landing-capability">What you can do with it</h2>
          <div className="landing-grid">
            {CAPABILITY_LIST.map((item) => (
              <article key={item.title} className="landing-card">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
          <p className="landing-more">
            <Link to="/features">Every feature in detail</Link>
          </p>
        </section>

        <section className="landing-section" aria-labelledby="landing-learn">
          <h2 id="landing-learn">Learn signal coordination with the tool</h2>
          <ul className="landing-learn-list">
            {LEARN_ROUTE_LIST.map((route) => (
              <li key={route.path}>
                <Link to={route.path} event="learn-open" source="landing">{route.label}</Link>
                <span>{route.learn?.summary}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="landing-section landing-tier" aria-labelledby="landing-free">
          <h2 id="landing-free">Free to start</h2>
          <p>
            The free tier saves up to {free.maxCorridors} corridors with {free.maxPlansPerCorridor}{' '}
            timing plans each and up to {free.maxIntersectionsPerCorridor} intersections per corridor,
            with CSV and JSON import and export, auto-sequence and share links. Pro raises the limits
            and adds PDF export and Synchro UTDF import.
          </p>
          <div className="landing-cta">
            <button className="landing-btn landing-btn-primary" onClick={onSignup}>Create a free account</button>
            <Link to="/pricing" className="landing-btn landing-btn-secondary">Compare tiers</Link>
          </div>
        </section>
      </div>
    </main>
  );
}
